import { NxGridModule } from "@allianz/ng-aquila/grid";
import { CommonModule } from "@angular/common";
import {
  AfterViewInit,
  Component,
  DestroyRef,
  inject,
  Input,
  OnChanges,
  OnInit,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Store } from "@ngxs/store";
import { LayoutBreakPointsService } from "@shared/services/layout-break-points.service";
import { Chart } from "chart.js/auto";
import ChartDataLabels from "chartjs-plugin-datalabels";

function roundPieChartValue(value, precision) {
  const multiplier = Math.pow(10, precision || 0);
  return Math.round(value * multiplier) / multiplier;
}

function getPieTooltipBody(bodyItem) {
  return bodyItem.lines;
}

@Component({
  selector: "app-pie-chart",
  templateUrl: "./pie-chart.component.html",
  styleUrls: ["./pie-chart.component.scss"],
  standalone: true,
  imports: [CommonModule, NxGridModule],
})
export class PieChartComponent implements OnInit, OnChanges, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);

  @Input() public refreshGraph;
  @Input() public chartId;

  private portfolioGwp;
  private pieChart: any;
  private portfolioLabels = [];
  private portfolioData = [];
  private portfolioPercentage = [];
  private portfolioDataInThousands = [];
  protected currentYear: any;
  protected currentYearTotal: any;

  private desktopView: boolean = false;
  protected isMobile: boolean;

  constructor(
    private readonly layoutBreakpointService: LayoutBreakPointsService,
    private readonly store: Store,
  ) {}

  ngOnInit(): void {
    this.isMobile = /iPhone|iPod|Android/i.test(navigator.userAgent);

    this.getPortFolioGwp();

    this.layoutBreakpointService
      .subscribeLayoutChanges()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.desktopView = this.layoutBreakpointService.renderDesktop();
      });
  }

  ngOnChanges(): void {
    if (this.refreshGraph) {
      if (this.pieChart != null) {
        this.pieChart.destroy();
        this.getPortFolioGwp();
        if (this.chartId) {
          this.createPieChart();
        }
      }
    }
  }

  ngAfterViewInit(): void {
    if (this.chartId) {
      this.createPieChart();
    }
  }

  private getPortFolioGwp(): void {
    this.portfolioLabels = [];
    this.portfolioData = [];
    this.portfolioPercentage = [];
    this.portfolioDataInThousands = [];

    this.portfolioGwp = this.store.selectSnapshot<any>(
      (state) => state.UserPerformanceState.gwpChartData.portFolioGwp,
    );
    this.currentYearTotal = this.store.selectSnapshot<any>(
      (state) => state.UserPerformanceState.gwpChartData.yearlyGwp,
    );

    this.portfolioGwp.forEach((i) => {
      this.portfolioLabels.push(i.name);
      this.portfolioData.push(i.value);
      this.portfolioPercentage.push(i.percentage);
    });

    this.portfolioData.forEach((i) => {
      if (i / 1000 < 1) {
        this.portfolioDataInThousands.push(roundPieChartValue(i / 1000, 1));
      } else {
        this.portfolioDataInThousands.push(roundPieChartValue(i / 1000, 0));
      }
    });

    this.currentYear = this.currentYearTotal[0].year;
    this.currentYearTotal = roundPieChartValue(
      this.currentYearTotal[0].totalValue / 1000,
      0,
    );
    this.currentYearTotal = this.currentYearTotal.toString();
    this.currentYearTotal = this.currentYearTotal.replace(
      /\B(?=(\d{3})+(?!\d))/g,
      ",",
    );
  }

  private createPieChart(): void {
    const ctx = document.getElementById(this.chartId) as HTMLCanvasElement;
    const getDesktopView = this.getDesktopView();
    const data = this.portfolioDataInThousands.map((item, index) => {
      return {
        data: item,
        percentage: this.portfolioPercentage[index],
        title: this.portfolioLabels[index],
      };
    });

    const getOrCreateLegendList = (_chart, id) => {
      const legendContainer = document.getElementById(id);
      if (!legendContainer) {
        return;
      }
      let listContainer = null;

      if (legendContainer.childNodes.length >= 1) {
        legendContainer.lastChild.remove();
      }

      if (!listContainer) {
        if (this.desktopView && !this.isMobile) {
          listContainer = document.createElement("ul");
        } else {
          listContainer = document.createElement("div");
        }
        listContainer.style.listStyleType = "none";

        legendContainer.appendChild(listContainer);
      }
      return listContainer;
    };

    const positionItem = (items, ul) => {
      let div = null;
      items.forEach((item, index) => {
        const boxSpan = document.createElement("span");
        boxSpan.style.background = item.fillStyle;
        boxSpan.style.borderColor = item.strokeStyle;
        boxSpan.style.borderWidth = item.lineWidth + "px";
        boxSpan.style.borderRadius = "100%";
        boxSpan.style.display = "inline-block";
        boxSpan.style.height = "10px";
        boxSpan.style.width = "9px";
        boxSpan.style.marginRight = "8px";
        boxSpan.style.position = "relative";

        if (this.desktopView && !this.isMobile) {
          item.pointStyle = "circle";
          const li = document.createElement("li");
          li.style.display = "flex";
          li.style.flexDirection = "row";
          li.style.marginBottom = "5px";
          boxSpan.style.top = "5px";

          const textContainer = document.createElement("p");
          textContainer.style.color = item.fontColor;

          const text = document.createTextNode(item.text);

          textContainer.appendChild(text);

          li.appendChild(boxSpan);
          li.appendChild(textContainer);
          ul.appendChild(li);
        } else {
          boxSpan.style.top = "3px";
          item.pointStyle = "circle";
          const p = document.createElement("p");

          if (index === 0 || index === 2) {
            div = document.createElement("div");
            div.style.display = "flex";
            div.style.flexDirection = "column";
            div.style.minWidth = "120px";
            div.style.paddingTop = "10px";
          }
          if (index === 0) {
            div.style.paddingRight = "20px";
          }
          if (index === 2 || index === 3) {
            p.style.marginLeft = "5px";
          }

          p.style.alignItems = "center";
          p.style.cursor = "pointer";
          p.classList.add("stacked-legend-li");

          p.style.paddingBottom = "0px";
          p.style.marginBottom = "0.5rem";
          p.classList.add("mobile-stacked-legend-li");
          const text = document.createTextNode(items[index].text);
          p.appendChild(boxSpan);
          p.appendChild(text);
          div.appendChild(p);
          ul.style.display = "flex";
          ul.style.flexDirection = "row";
          ul.style.justifyContent = "center";
          ul.style.position = "relative";

          if (window.innerWidth > 479.98) {
            ul.style.top = "15px";
            const bottomText = document.getElementById("bottom-text");
            bottomText.style.paddingTop = "55px";
          }
          ul.appendChild(div);
        }
      });
    };

    const htmlLegendPlugin = {
      id: "htmlLegend",
      afterUpdate(chart, args, _options) {
        if (args.mode === "resize" || args.mode === "attach") {
          return;
        }
        const ul = getOrCreateLegendList(chart, "pie-legend");
        const showLegend = true;
        let filteredLegend =
          chart.$datalabels._labels[0].$context.dataset.data.filter(
            (item) => item.data > 0,
          );
        filteredLegend = filteredLegend.map((item) => item.title);
        ul.style.flexDirection = "column";

        if (showLegend) {
          const items =
            chart.options.plugins.legend.labels.generateLabels(chart);
          const filteredItems = items.filter((item) =>
            filteredLegend.some((legend) => legend === item.text),
          );
          positionItem(filteredItems, ul);
        }
      },
    };

    this.pieChart = new Chart(ctx, {
      plugins: [ChartDataLabels, htmlLegendPlugin],
      type: "pie",
      data: {
        labels: this.portfolioLabels,
        datasets: [
          {
            data: data,
            borderWidth: 1,
            borderColor: "white",
            backgroundColor: [
              "#007AB3",
              "#8A679C",
              "#FDD25C",
              "#7FE4E0",
              "#FF934F",
              "#CCDD61",
            ],
          },
        ],
      },

      options: {
        layout: {
          padding: {
            left: 10,
            bottom: 10,
          },
        },
        parsing: {
          key: "data",
        },
        maintainAspectRatio: false,
        responsive: true,

        plugins: {
          datalabels: {
            offset: 14,
            color: "#FFFFFF",
            align: "end",
            font: {
              size: getDesktopView ? 14 : 12,
            },
            formatter: function (value, chart) {
              const index = chart.chart.data.datasets[0].data.indexOf(value);
              const percent =
                chart.chart.data.datasets[0].data[index]["percentage"].toFixed(
                  1,
                );
              const returnValue = percent + "%";

              if (value.data !== 0) {
                return returnValue;
              } else {
                return "";
              }
            },
          },
          legend: {
            display: false,
          },
          tooltip: {
            enabled: false,
            yAlign: "bottom",
            xAlign: "center",
            position: "nearest",

            external: (context) => {
              let tooltipEl = document.getElementById("pie-tooltip");
              if (!tooltipEl) {
                tooltipEl = document.createElement("div");
                tooltipEl.id = "pie-tooltip";
                tooltipEl.innerHTML = "<div></div>";
                document.body.appendChild(tooltipEl);
              }

              const tooltipModel = context.tooltip;
              if (tooltipModel.opacity === 0) {
                tooltipEl.style.opacity = "0";
                return;
              }

              if (tooltipModel.body) {
                const bodyLines = tooltipModel.body.map(getPieTooltipBody);
                let innerHtml = '<div class="pie-tooltip-title">';
                const raw: any = tooltipModel.chart.tooltip.dataPoints[0].raw;

                const titleDiv = raw.title + "</div>";
                const span =
                  '<span class="pie-tooltip-text">RM ' +
                  bodyLines.toString() +
                  "K (" +
                  raw.percentage +
                  "%)";
                innerHtml +=
                  titleDiv + span + '</span> <div class="pie-caret"></div>';

                tooltipEl.innerHTML = innerHtml;
              }

              tooltipEl.style.position = "absolute";

              tooltipEl.style.left = !this.isMobile
                ? "calc( 1px + " + (tooltipModel.caretX - 53) + "px)"
                : tooltipModel.caretX - 62 + "px";

              tooltipEl.style.top = !this.isMobile
                ? tooltipModel.caretY - 46 + "px"
                : tooltipModel.caretY - 21 + "px";

              tooltipEl.style.opacity = "1";
              tooltipEl.style.boxShadow = "0px 2px 4px #41414180";
              tooltipEl.style.width = "166px";
              tooltipEl.style.height = "90px";
              tooltipEl.style.pointerEvents = "none";
              tooltipEl.style.backgroundColor = "#FFF";
              tooltipEl.style.borderRadius = "3px";
              tooltipEl.style.zIndex = "10";
              tooltipEl.style.whiteSpace = "nowrap";
            },
          },
        },

        animation: {
          duration: 1000,
          easing: "easeInOutBounce",
        },

        elements: {
          line: {
            fill: false,
            tension: 0,
          },
        },

        scales: {},
      },
    });
  }

  private getDesktopView(): boolean {
    return this.desktopView;
  }
}
